import React, { useState, useEffect } from 'react';
import './index.css';
import TeamInput from './components/TeamInput';
import TeamPerformanceReview from './components/TeamPerformanceReview';
import SprintMood from './components/SprintMood';
import TeamMotivation from './components/TeamMotivation';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { realHoroscopes } from './realHoroscopes';
import { getDailySignFunLines } from './openai';

// Horoscope dictionary (same as TeamHoroscope)
const zodiacDescriptions = {
  Aries: [
    "Charged into production without tests. Bold and buggy.",
    "Took initiative on a risky refactor. Fearless, but QA is nervous.",
    "Energized the team with wild ideas. Needs a leash on deployments."
  ],
  Taurus: [
    "Refused to refactor. Stubbornly stable.",
    "Kept the codebase grounded. Slow to change, but reliable.",
    "Insisted on using the same stack. Loyal to legacy code."
  ],
  Gemini: [
    "Pair programmed with themselves. Had a debate in the PR.",
    "Switched tasks mid-sprint. Multitasking master or chaos agent?",
    "Wrote both the bug and the fix. Loves a good chat in Slack."
  ],
  Cancer: [
    "Cried at merge conflicts. Cares deeply about the codebase.",
    "Brought snacks to the retro. Emotional support for the team.",
    "Protected the repo like family. Sensitive to code reviews."
  ],
  Leo: [
    "Polished every commit message. Needs to go outside.",
    "Presented the demo with flair. Loves the spotlight.",
    "Added their name to the About page. Seeks recognition."
  ],
  Virgo: [
    "Linted the README. Organized the Jira board for fun.",
    "Spotted every typo. Perfectionist in code and comments.",
    "Refactored the CSS for pixel-perfect alignment."
  ],
  Libra: [
    "Balanced the sprint. Can't decide between tabs or spaces.",
    "Mediated a debate over code style. Seeks harmony in the repo.",
    "Split the difference on every estimate. Diplomatic and fair."
  ],
  Scorpio: [
    "Secretly rewrote the backend. Mysterious git history.",
    "Fixed a critical bug at midnight. Intense and focused.",
    "Left cryptic comments. Keeps everyone guessing."
  ],
  Sagittarius: [
    "Deployed on Friday. Loves chaos.",
    "Suggested a hackathon. Adventurous and optimistic.",
    "Pushed to production from the airport. Always on the move."
  ],
  Capricorn: [
    "Scheduled a meeting for everything. Actually enjoys Jira.",
    "Hit every deadline. Ambitious and disciplined.",
    "Wrote a project plan for the project plan."
  ],
  Aquarius: [
    "Suggested blockchain for the standup notes.",
    "Proposed a new workflow. Innovative and quirky.",
    "Started a side project during the sprint."
  ],
  Pisces: [
    "Dreamed up new features. Forgot to write tests.",
    "Designed a beautiful UI. Artistic and imaginative.",
    "Lost in thought during standup. Needs grounding."
  ],
};

const zodiacRanges = [
  { sign: 'Capricorn', start: '01-01', end: '01-19' },
  { sign: 'Aquarius', start: '01-20', end: '02-18' },
  { sign: 'Pisces', start: '02-19', end: '03-20' },
  { sign: 'Aries', start: '03-21', end: '04-19' },
  { sign: 'Taurus', start: '04-20', end: '05-20' },
  { sign: 'Gemini', start: '05-21', end: '06-20' },
  { sign: 'Cancer', start: '06-21', end: '07-22' },
  { sign: 'Leo', start: '07-23', end: '08-22' },
  { sign: 'Virgo', start: '08-23', end: '09-22' },
  { sign: 'Libra', start: '09-23', end: '10-22' },
  { sign: 'Scorpio', start: '10-23', end: '11-21' },
  { sign: 'Sagittarius', start: '11-22', end: '12-21' },
  { sign: 'Capricorn', start: '12-22', end: '12-31' },
];

function getZodiacSign(birthdate) {
  if (!birthdate) return '';
  const [year, month, day] = birthdate.split('-').map(Number);
  const mmdd = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  for (const range of zodiacRanges) {
    if (mmdd >= range.start && mmdd <= range.end) {
      return range.sign;
    }
  }
  return '';
}

function App() {
  const [members, setMembers] = useState([]);
  const [seriousReviews, setSeriousReviews] = useState([]);
  const [dailyFunLines, setDailyFunLines] = useState({});
  // Set your background image URL here (edit inline)
  const skyBgUrl = "https://cdn.pixabay.com/photo/2017/01/31/13/40/shooting-star-2024127_1280.png"; // Unsplash night sky
  const bgUrl = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRfIZrYHtu0KbjdeYuv9AEBTSOTNtny3j2yyA&s"; // Bank of Georgia logo

  // Stylized sky image background with logo overlay
  const mainBg = {
    backgroundImage: `url(${skyBgUrl}), url(${bgUrl})`,
    backgroundSize: 'cover, 180px',
    backgroundPosition: 'center, top right',
    backgroundRepeat: 'no-repeat, no-repeat',
    backgroundColor: '#232946',
  };

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          birthdate: data.birthdate && data.birthdate.toDate ? data.birthdate.toDate().toISOString().slice(0, 10) : '',
          role: data.role || 'developer',
        };
      });
      setMembers(users);
    });
    return () => unsubscribe();
  }, []);

  // dailyFunLines are fetched once per day and passed to TeamPerformanceReview. Fun lines are NOT generated per user.
  useEffect(() => {
    async function fetchFunLines() {
      const lines = await getDailySignFunLines();
      setDailyFunLines(lines);
    }
    fetchFunLines();
  }, []);

  // Collect horoscopes for each member
  const horoscopes = members.map(m => {
    const sign = getZodiacSign(m.birthdate);
    const realHoro = realHoroscopes[sign] || "";
    return `${m.name} (${sign}): ${realHoro}`;
  });

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={mainBg}
    >
      {/* Background stars/planets/solar system */}
      <div className="pointer-events-none absolute inset-0 z-0">
        {/* Enhanced Starfield */}
        {Array.from({ length: 180 }).map((_, i) => {
          const size = Math.random() * 1.8 + 0.7;
          const color = [
            'rgba(255,255,255,0.85)', // white
            'rgba(255,200,120,0.7)',  // orange
            'rgba(120,180,255,0.6)',  // blue
            'rgba(255,255,255,0.4)',  // faint white
            'rgba(255,180,80,0.5)'    // faint orange
          ][Math.floor(Math.random() * 5)];
          const blur = Math.random() > 0.8 ? '2px' : '0.5px';
          return (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: `${size}px`,
                height: `${size}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                background: color,
                filter: `blur(${blur}) drop-shadow(0 0 2px ${color})`,
                opacity: Math.random() * 0.7 + 0.2
              }}
            />
          );
        })}
        {/* Planets and solar system orbits */}
        <div className="relative w-[420px] h-[420px] animate-spin-slow2">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-5xl" style={{transform: 'rotate(0deg) translateY(-210px)'}}>🪐</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-3xl" style={{transform: 'rotate(60deg) translateY(-170px)'}}>🛰️</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-4xl" style={{transform: 'rotate(120deg) translateY(-130px)'}}>🪐</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-2xl" style={{transform: 'rotate(180deg) translateY(-90px)'}}>🪐</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-4xl" style={{transform: 'rotate(240deg) translateY(-170px)'}}>🛸</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 text-3xl" style={{transform: 'rotate(300deg) translateY(-130px)'}}>🪐</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-6xl animate-pulse">☀️</div>
          <div className="absolute left-1/2 top-0 -translate-x-1/2 w-full h-full border border-dashed border-indigo-400/20 rounded-full"></div>
        </div>
        {/* More cosmic icons */}
        <div className="absolute top-10 left-10 text-6xl opacity-20 animate-spin-slow">🪐</div>
        <div className="absolute bottom-20 right-20 text-5xl opacity-20 animate-pulse">✨</div>
        <div className="absolute top-1/2 left-1/3 text-4xl opacity-10 animate-bounce">🌟</div>
        <div className="absolute bottom-10 left-1/4 text-7xl opacity-10 animate-spin">🌙</div>
      </div>
      <main className="w-full max-w-5xl space-y-10 z-10 bg-[#181c2a] rounded-3xl shadow-2xl p-6 border border-orange-300">
        {/* AI Team Motivation (ChatGPT) at the top */}
        <section className="bg-[#232946] rounded-2xl shadow-xl p-8 border border-orange-300">
          <TeamMotivation horoscopes={seriousReviews} members={members} />
        </section>
        {/* Sprint Mood Section (uncomment and style if needed) */}
        {/* <section className="bg-[#232946] rounded-2xl shadow-xl p-8 border border-orange-300">
          <div className="flex items-center justify-center gap-4 mb-2 flex-wrap">
            <span className="text-4xl">🔮</span>
            <h1 className="text-4xl font-extrabold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-400 to-purple-500 font-serif drop-shadow-lg whitespace-nowrap">iBank Astrology</h1>
            <span className="text-4xl">🌠</span>
          </div>
          <SprintMood />
        </section> */}
        {/* Team Performance Review */}
        <section className="bg-[#232946] rounded-2xl shadow-xl p-8 border border-orange-300">
          <div className="text-2xl font-bold mb-4 font-serif text-orange-200 flex items-center gap-2">🌌 გუნდის წევრების ასტროლოგიური პროგნოზი</div>
          <TeamPerformanceReview members={members} onSeriousReviews={setSeriousReviews} dailyFunLines={dailyFunLines} />
        </section>
        {/* Team Input Section (moved to bottom) */}
        <section className="bg-[#232946] rounded-2xl shadow-xl p-8 border border-orange-300">
          <div className="text-2xl font-bold mb-4 font-serif text-orange-200 flex items-center gap-2">📝 Add Team Members</div>
          <TeamInput members={members} setMembers={setMembers} />
        </section>
      </main>
      <footer className="mt-12 text-orange-700 text-xs z-10 font-semibold">© {new Date().getFullYear()} iBank Astrology ✦ Powered by Bank of Georgia</footer>
      {/* Add some custom keyframes for slow spin */}
      <style>{`
        @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 18s linear infinite; }
        @keyframes spin-slow2 { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .animate-spin-slow2 { animation: spin-slow2 60s linear infinite; }
      `}</style>
    </div>
  );
}

export default App;
